import { useRollbar } from '@rollbar/react';

/** Clerk handshake 404s can recover on reload; cap retries in session storage so permanent failures surface. */
const RELOAD_KEY = 'dm-handshake-reloads';
const MAX_RELOADS = 2;
/** A reload never returns to the call that triggered it, so `clear()` cannot run for a recovery that WORKED — the counter would otherwise persist for the tab's lifetime and let two unrelated transient failures trip the loop limit. Only count reloads that happen close together: a real loop re-fires within seconds, whereas separate incidents are minutes apart. */
const LOOP_WINDOW_MS = 30_000;

export const useHandshakeRecovery = () => {
  const rollbar = useRollbar();

  const read = (now: number) => {
    try {
      const raw = window.sessionStorage.getItem(RELOAD_KEY);
      if (!raw) return 0;
      const { n, ts } = JSON.parse(raw) as { n: number; ts: number };
      if (!Number.isFinite(n) || !Number.isFinite(ts)) return 0;
      return now - ts > LOOP_WINDOW_MS ? 0 : n;
    } catch {
      return 0; // private mode, storage disabled, or a legacy/corrupt value
    }
  };

  const clear = () => {
    try {
      window.sessionStorage.removeItem(RELOAD_KEY);
    } catch {}
  };

  const recover = (err: unknown, phase: string): boolean => {
    const now = Date.now();
    const count = read(now) + 1;
    try {
      window.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ n: count, ts: now }));
    } catch {}
    if (count > MAX_RELOADS) {
      rollbar.error('[auth] setActive handshake stuck in reload loop', {
        phase,
        reloads: count,
        error: String(err),
      });
      return false;
    }
    rollbar.warning('[auth] setActive handshake failed, reloading', {
      phase,
      reloads: count,
      error: String(err),
    });
    window.location.reload();
    return true;
  };

  return { recover, clear };
};
